import { Box, Checkbox, Loader, NumberInput, Select, TextInput } from '@mantine/core';
import { useApi } from 'api/api-context';
import { DateInput } from 'components/inputs/DateInput';
import panic from 'errors/panic';
import { useEffect, useMemo, useState } from 'react';

export interface EvidenceValueDefinition {
  fieldName?: string;
  fieldType?: string;
  isRequired?: boolean;
  precision?: number;
  selectOptions?: string[];
  predefinedDataTypeId?: number;
}

interface EvidenceValueInputProps {
  definition: EvidenceValueDefinition;
  value: string | number | boolean | null | undefined;
  onChange: (value: string | number | boolean | null) => void;
  error?: React.ReactNode;
  disabled?: boolean;
  hideLabel?: boolean;
}

type SelectOption = { value: string; label: string; group?: string };

/** Renders the controlled input selected by an evidence-field definition. */
export default function EvidenceValueInput({
  definition,
  value,
  onChange,
  error,
  disabled,
  hideLabel,
}: EvidenceValueInputProps) {
  const { getAction } = useApi();
  const [predefinedOptions, setPredefinedOptions] = useState<SelectOption[]>([]);
  const [predefinedLoading, setPredefinedLoading] = useState(false);
  const [predefinedError, setPredefinedError] = useState<string | null>(null);
  const stringValue = value === null || value === undefined ? '' : String(value);

  useEffect(() => {
    let active = true;
    if (definition.fieldType !== 'predefined' || !definition.predefinedDataTypeId) {
      setPredefinedOptions([]);
      setPredefinedError(null);
      return () => {
        active = false;
      };
    }

    setPredefinedLoading(true);
    setPredefinedError(null);
    getAction('PredefinedDataGetMixed')({
      parameters: { predefinedDataTypeId: String(definition.predefinedDataTypeId) },
    })
      .then((options) => {
        if (!active) return;
        setPredefinedOptions(
          options.map((option) => ({
            value: option.key,
            label: option.value ? `${option.key} — ${option.value}` : option.key,
            group: option.origin === 'user' ? 'Vlastné' : 'Globálne',
          }))
        );
      })
      .catch((caught) => {
        if (!active) return;
        panic(caught as Error);
        setPredefinedError('Preddefinované údaje sa nepodarilo načítať.');
      })
      .finally(() => {
        if (active) setPredefinedLoading(false);
      });

    return () => {
      active = false;
    };
  }, [definition.fieldType, definition.predefinedDataTypeId, getAction]);

  const selectData = useMemo<SelectOption[]>(() => {
    const configured = (definition.selectOptions ?? []).map((option) => ({ value: option, label: option }));
    if (stringValue && !configured.some((option) => option.value === stringValue)) {
      return [{ value: stringValue, label: stringValue, group: 'Uložená hodnota' }, ...configured];
    }
    return configured;
  }, [definition.selectOptions, stringValue]);

  const predefinedData = useMemo(() => {
    if (stringValue && !predefinedOptions.some((option) => option.value === stringValue)) {
      return [{ value: stringValue, label: stringValue, group: 'Uložená hodnota' }, ...predefinedOptions];
    }
    return predefinedOptions;
  }, [predefinedOptions, stringValue]);

  const commonProps = {
    size: 'lg' as const,
    label: hideLabel ? undefined : definition.fieldName,
    'aria-label': definition.fieldName,
    disabled,
  };
  const inputProps = { ...commonProps, withAsterisk: definition.isRequired };

  switch (definition.fieldType) {
    case 'text':
      return (
        <TextInput
          {...inputProps}
          placeholder={definition.fieldName}
          required={definition.isRequired}
          value={stringValue}
          error={error}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      );
    case 'number':
      return (
        <NumberInput
          {...inputProps}
          placeholder={definition.fieldName}
          required={definition.isRequired}
          value={value === null || value === undefined || value === '' ? '' : Number(value)}
          error={error}
          hideControls
          precision={definition.precision}
          step={definition.precision ? 1 / Math.pow(10, definition.precision) : 1}
          onChange={onChange}
        />
      );
    case 'date':
      return (
        <DateInput
          {...inputProps}
          placeholder={definition.fieldName}
          required={definition.isRequired}
          value={stringValue}
          error={error}
          onChange={(next) => onChange(next ?? '')}
        />
      );
    case 'select':
      return (
        <Select
          {...inputProps}
          value={stringValue || null}
          data={selectData}
          error={error}
          clearable={!definition.isRequired}
          onChange={(next) => onChange(next ?? '')}
        />
      );
    case 'predefined':
      return (
        <Select
          {...inputProps}
          value={stringValue || null}
          data={predefinedData}
          rightSection={predefinedLoading ? <Loader size="sm" /> : undefined}
          error={error ?? predefinedError}
          clearable={!definition.isRequired}
          searchable
          onChange={(next) => onChange(next ?? '')}
        />
      );
    case 'checkbox':
      return (
        <Checkbox
          {...commonProps}
          checked={value === true}
          error={error}
          onChange={(event) => onChange(event.currentTarget.checked)}
        />
      );
    default:
      return <Box>Neznámy typ evidenčného poľa {definition.fieldType}</Box>;
  }
}
